/* global React, Icon, Card, Button, IconButton, Field, Input, Select, Currency, Switch, Badge, Checkbox, DataList, Modal, PayDialog, PaySubHead, EMPLOYEES, MILEAGE_RATE, toast, fmt */
// ============================================================
// PLUMME 2.0 — Reiser og utlegg (travel & expenses)
// Modelled on the Plumme payroll "Reiser og utlegg" feature
// (docs/session-notes/2026-06-02-payroll-travel-handoff.md):
// travel reports with mileage + expense lines, an
// Utkast → Ikke behandlet → Godkjent lifecycle, and a
// private-vs-firmakort payroll-reimbursement split.
// Employer view reads submitted employee portal travels from backend app-state.
// ============================================================
const { useState: useTravelState, useMemo: useTravelMemo, useEffect: useTravelEffect } = React;

const TRAVEL_RATE = (typeof MILEAGE_RATE === 'number' ? MILEAGE_RATE : 3.5); // Default tax-free allowance, not the government travel agreement.
const TRAVEL_COUNTRIES = ['Norge', 'Sverige', 'Danmark', 'Tyskland', 'Storbritannia', 'Annet'];
const TRAVEL_VEHICLES = ['Bil (også el-bil)', 'Egen bil', 'El-bil', 'Motorsykkel', 'Annet'];
const OPPHOLD_TYPES = ['Hotell', 'Annen overnatting m/kokemuligheter', 'Annen overnatting u/kokemuligheter', 'Pensjonat eller brakke'];
const OPPHOLD_RATES = { Hotell: 693, 'Annen overnatting m/kokemuligheter': 107, 'Annen overnatting u/kokemuligheter': 400, 'Pensjonat eller brakke': 400 };
const TRAVEL_OVERNIGHT_LABEL = 'Reise med overnatting';
const MEAL_DEDUCT = { frokost: 0.2, lunsj: 0.3, middag: 0.5 };
const EXPENSE_CATEGORIES = [
  'Mat og drikke (på reise)', 'Bompenger', 'Kollektivtransport', 'Ferge', 'Taxi', 'Fly', 'Overtidsmat',
  'Parkering', 'Overnatting', 'Representasjon', 'Leiebil på reise', 'Drivstoff til leiebil',
  'Programvare', 'Kurs og faglig oppdatering', 'Annet',
];
const TRAVEL_STATUS = { draft: ['warning', 'Utkast'], submitted: ['neutral', 'Ikke behandlet'], approved: ['success', 'Godkjent'], rejected: ['error', 'Avvist'] };

function loadTravels() {
  if (window.PlummeBackend?.appState) window.PlummeBackend.publishGlobals?.(window.PlummeBackend.appState);
  return Array.isArray(window.PAY_TRAVELS) ? window.PAY_TRAVELS : [];
}
function tDate(iso) { if (!iso) return '—'; const [y, m, d] = iso.split('-'); return `${d}.${m}.${y}`; }
function tPeriod(trip) { return trip.dateFrom === trip.dateTo || !trip.dateTo ? tDate(trip.dateFrom) : tDate(trip.dateFrom) + ' – ' + tDate(trip.dateTo); }
function genId(p) { return p + Date.now().toString(36) + Math.floor(Math.random() * 1000); }
function todayTravelIso() { return new Date().toISOString().slice(0, 10); }
function travelDatesBetween(a, b) {
  const out = [];
  if (!a) return out;
  const fmtIso = (date) => date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
  const start = new Date(a + 'T00:00:00');
  const end = new Date((b || a) + 'T00:00:00');
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [a];
  for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) out.push(fmtIso(date));
  return out;
}
function accommodationDiet(type, days, mealVal) {
  const rate = OPPHOLD_RATES[type] || 0;
  return Math.round(days.reduce((sum, date) => {
    let day = rate;
    if (!mealVal(date, 'frokost')) day -= rate * MEAL_DEDUCT.frokost;
    if (!mealVal(date, 'lunsj')) day -= rate * MEAL_DEDUCT.lunsj;
    if (!mealVal(date, 'middag')) day -= rate * MEAL_DEDUCT.middag;
    return sum + Math.max(0, day);
  }, 0) * 100) / 100;
}
function accommodationAmount(line = {}) { return Number(line.payrollAmount ?? line.allowanceAmount ?? line.amount ?? 0) || 0; }
function employees() {
  if (window.PlummeBackend?.appState) window.PlummeBackend.publishGlobals?.(window.PlummeBackend.appState);
  return Array.isArray(window.EMPLOYEES) ? window.EMPLOYEES : [];
}
function travelPayout(trip) {
  const reimb = travelTotals(trip).reimb;
  if (trip.settled || trip._backend?.status === 'included-in-payroll') return ['success', 'Utbetalt'];
  if (trip.status === 'approved' && reimb > 0) return ['warning', 'Til utbetaling'];
  if (reimb === 0) return ['neutral', 'Ingen utbetaling'];
  return ['neutral', 'Avventer'];
}

function mileageAmount(m) { return Math.round((+m.km || 0) * (m.returnTrip ? 2 : 1) * (m.rate || TRAVEL_RATE)); }
function travelTotals(trip) {
  const mil = (trip.mileage || []).reduce((s, m) => s + (m.amount || 0), 0);
  const privTolls = (trip.mileage || []).reduce((s, m) => s + (m.tollsPaidBy === 'Privat utlegg' ? (+m.tolls || 0) : 0), 0);
  const compTolls = (trip.mileage || []).reduce((s, m) => s + (m.tollsPaidBy !== 'Privat utlegg' ? (+m.tolls || 0) : 0), 0);
  const priv = (trip.expenses || []).filter((e) => e.paymentMethod === 'Privat utlegg').reduce((s, e) => s + (e.amount || 0), 0);
  const comp = (trip.expenses || []).filter((e) => e.paymentMethod !== 'Privat utlegg').reduce((s, e) => s + (e.amount || 0), 0);
  const accom = (trip.accommodation || []).reduce((s, line) => s + accommodationAmount(line), 0);
  return { mil, privTolls, compTolls, priv, comp, accom, reimb: mil + privTolls + priv + accom, company: comp + compTolls, lines: (trip.mileage || []).length + (trip.expenses || []).length + (trip.accommodation || []).length };
}

const travelApprovalLabel = (travel) => t(travel.expenses?.length && !travel.mileage?.length && !travel.accommodation?.length && !travel._backend?.overnight ? 'trv.approveExpense' : 'trv.approveTravel');
function ApproveDialog({ travel, totals, onClose, onApprove }) {
  const postings = [];
  if (totals.mil > 0) postings.push(['7150', 'Kilometergodtgjørelse, trekkfri', totals.mil]);
  if (totals.accom > 0) postings.push(['7150', 'Diett ved overnatting, trekkfri', totals.accom]);
  if (totals.privTolls > 0) postings.push(['7140', 'Reisekostnad, bompenger', totals.privTolls]);
  (travel.expenses || []).forEach((e) => postings.push([e.paymentMethod === 'Privat utlegg' ? '7140' : '7140', 'Reisekostnad — ' + e.category, e.amount]));
  return <PayDialog open onClose={onClose} title={'Godkjenn ' + travel.num} max={680} saveLabel={travelApprovalLabel(travel)} onSave={onApprove}>
    <div className="grid-2 gap-16">
      <div>
        <div className="eyebrow" style={{ marginBottom: 8 }}>{t('trv.postingSuggestion')}</div>
        <div className="col gap-8">
          {postings.map(([nr, name, amt], i) => <div key={i} className="row between gap-12" style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--r-ms)' }}>
            <div><b className="t-sm">{nr}</b> <span className="t-sm muted">{name}</span></div><b className="t-num">{fmt(amt)}</b>
          </div>)}
          <div className="row between gap-12" style={{ padding: '10px 12px', borderRadius: 'var(--r-ms)', background: 'var(--panel)' }}>
            <div><b className="t-sm">2940</b> <span className="t-sm muted">{t('trv.owedToEmployee')}</span></div><b className="t-num">{fmt(totals.reimb)}</b>
          </div>
        </div>
      </div>
      <div>
        <div className="eyebrow" style={{ marginBottom: 8 }}>{t('pay.travelClaim')}</div>
        <div className="panel center" style={{ minHeight: 180, flexDirection: 'column', gap: 8, padding: 18, textAlign: 'center' }}>
          <Icon name="fileText" size={28} />
          <div className="t-sm muted" style={{ fontWeight: 700 }}>{t('trv.previewComing')}</div>
        </div>
      </div>
    </div>
    <div className="t-sm muted" style={{ fontWeight: 600 }}>{t('sharedText.trv.privateRefundOf')} <b>{fmt(totals.reimb)}</b>{' '}{t('trv.privateRefundSuffix')}</div>
  </PayDialog>;
}

function RejectTravelDialog({ travel, onClose, onReject }) {
  const [note, setNote] = useTravelState('');
  const trimmed = String(note || '').trim();
  return <PayDialog open onClose={onClose} title={'Avvis ' + (travel.num || 'reise')} max={560} saveLabel="Avvis reise" onSave={() => {
    if (!trimmed) {
      toast(t('trv.rejectReasonRequired'), 'warning');
      return;
    }
    onReject(trimmed);
  }}>
    <div className="col gap-14">
      <div className="t-sm muted" style={{ fontWeight: 700 }}>{t('trv.reasonShownToEmployee')}</div>
      <Field label={t('trv.reason')} help={t('helpTopics.travelApprove.body')} helpTitle={t('helpTopics.travelApprove.title')}>
        <textarea className="input" rows={4} value={note} onChange={(event) => setNote(event.target.value)} placeholder={t('trv.whatMustBeFixed')} />
      </Field>
    </div>
  </PayDialog>;
}

function PayTravel() {
  const [travels, setTravelsState] = useTravelState(() => loadTravels());
  const [detailId, setDetailId] = useTravelState(()=>{const id=window.__plummeTravelFocus||null;window.__plummeTravelFocus=null;return id;});
  const [dialog, setDialog] = useTravelState(null);
  const [empFilter, setEmpFilter] = useTravelState('alle');
  const [q, setQ] = useTravelState('');
  const [showDone, setShowDone] = useTravelState(true);
  const pendingTravelsRef = React.useRef(new Map());
  const mergePendingTravels = (rows) => {
    const now = Date.now();
    const merged = new Map((rows || []).map((travel) => [travel.id, travel]));
    pendingTravelsRef.current.forEach((entry, id) => {
      if (!entry || entry.expiresAt < now) {
        pendingTravelsRef.current.delete(id);
        return;
      }
      merged.set(id, entry.travel);
    });
    return [...merged.values()];
  };
  const rememberSavedTravel = (saved) => {
    if (!saved?.id) return saved;
    pendingTravelsRef.current.set(saved.id, { travel: saved, expiresAt: Date.now() + 15000 });
    return saved;
  };

  useTravelEffect(() => {
    const sync = () => setTravelsState(mergePendingTravels(loadTravels()));
    window.addEventListener('plumme-backend-state', sync);
    window.addEventListener('plumme-employees-changed', sync);
    return () => {
      window.removeEventListener('plumme-backend-state', sync);
      window.removeEventListener('plumme-employees-changed', sync);
    };
  }, []);

  if (detailId) {
    const t = travels.find((x) => x.id === detailId);
    if (!t) { setDetailId(null); return null; }
    return <BackendTravelDetail travel={t} onBack={() => setDetailId(null)} onSaved={(saved) => {
      const nextSaved = rememberSavedTravel(saved);
      setTravelsState((current) => current.some((item) => item.id === nextSaved.id)
        ? current.map((item) => item.id === nextSaved.id ? nextSaved : item)
        : [nextSaved, ...current]);
      setDetailId(nextSaved.id);
    }} onDeleted={() => { setTravelsState(loadTravels()); setDetailId(null); }} />;
  }

  const filtered = travels.filter((trip) => (empFilter === 'alle' || trip.employee === empFilter)
    && (showDone || trip.status !== 'approved')
    && (!q || (trip.num + ' ' + trip.purpose + ' ' + trip.employee).toLowerCase().includes(q.toLowerCase())));

  return <Card>
    <PaySubHead title={t('pay.travelAndExpenses')} sub={t('trv.subShort')}
      action={<div className="row gap-10">
        <Button variant="secondary" icon="receipt" disabled={!employees().length} onClick={() => setDialog('expense')}>{t('pay.newExpense')}</Button>
        <Button icon="plus" disabled={!employees().length} onClick={() => setDialog('travel')}>{t('trv.newTravel')}</Button>
      </div>} />

    <div className="row wrap gap-10" style={{ marginBottom: 16 }}>
      <Select value={empFilter} onChange={(e) => setEmpFilter(e.target.value)} style={{ maxWidth: 220 }}>
        <option value="alle">{t('trv.allEmployees')}</option>
        {employees().map((e) => <option key={e.id} value={e.name}>{e.name}</option>)}
      </Select>
      <div className="grow" style={{ minWidth: 180, maxWidth: 320 }}><Input icon="search" placeholder={t('trv.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} /></div>
      <label className="row gap-8 t-sm" style={{ fontWeight: 700, cursor: 'pointer', marginLeft: 'auto' }}><Switch on={showDone} onChange={setShowDone} /> {t('trv.showApproved')}</label>
    </div>

    {filtered.length === 0
      ? <div className="panel" style={{ padding: '34px 18px', textAlign: 'center' }}><div className="t-h3">{t('trv.noTravel')}</div><div className="t-sm muted mt-4" style={{ fontWeight: 600 }}>{t('trv.getStarted')}</div></div>
      : <DataList rows={filtered} rowKey={(trip) => trip.id} onRowClick={(trip) => setDetailId(trip.id)}
        columns={[
          { header: 'Reise', sort: (trip) => trip.purpose, render: (trip) => <div className="row gap-12">
            <span className="icon-chip"><Icon name="car" size={18} /></span>
            <div><div className="t-h3" style={{ fontSize: 14.5 }}>{trip.purpose || 'Uten formål'}</div><div className="t-sm muted">{trip.num} · {t('trv.tripCounts', { mileage: (trip.mileage || []).length, stays: (trip.accommodation || []).length, expenses: (trip.expenses || []).length })}</div></div>
          </div> },
          { header: 'Ansatt', cellClass: 't-sm', sort: (trip) => trip.employee, render: (trip) => <span style={{ fontWeight: 700 }}>{trip.employee}</span> },
          { header: 'Periode', cellClass: 't-sm muted', sort: (trip) => trip.dateFrom, render: (trip) => tPeriod(trip) },
          { header: 'Til lønn', headerRight: true, cellClass: 'num', sort: (trip) => travelTotals(trip).reimb, render: (trip) => fmt(travelTotals(trip).reimb) },
          { header: 'Status', sort: (trip) => (TRAVEL_STATUS[trip.status] || TRAVEL_STATUS.submitted)[1], render: (trip) => { const [v, l] = TRAVEL_STATUS[trip.status] || TRAVEL_STATUS.submitted; return <Badge variant={v} dot>{l}</Badge>; } },
          { header: 'Oppgjør', sort: (trip) => travelPayout(trip)[1], render: (trip) => { const [v, l] = travelPayout(trip); return <Badge variant={v} dot>{l}</Badge>; } },
        ]} />}

    {dialog === 'travel' && <BackendNewTravelDialog onClose={() => setDialog(null)} onCreate={(saved) => { const nextSaved = rememberSavedTravel(saved); setTravelsState((current) => [nextSaved, ...current.filter((entry) => entry.id !== nextSaved.id)]); setDialog(null); setDetailId(nextSaved.id); }} />}
    {dialog === 'expense' && <ExpenseDialog standalone onClose={() => setDialog(null)} onSave={async (exp, employee) => {
      try {
        const saved = await window.PlummeBackend.saveEmployeePortalTravel({
          employeeId: employee.id,
          employeeName: employee.name,
          purpose: exp.description || 'Utlegg',
          destination: 'Norge',
          dateFrom: exp.date,
          dateTo: exp.date,
          status: 'draft',
          expenseLines: [exp],
        });
        toast(t('trv.expenseSaved'), 'success');
        const nextSaved = rememberSavedTravel(saved);
        setTravelsState((current) => [nextSaved, ...current.filter((entry) => entry.id !== nextSaved.id)]);
        setDialog(null);
        setDetailId(nextSaved.id);
      } catch (error) {
        toast(error?.message || t('trv.expenseSaveFailed'), 'error');
      }
    }} />}
  </Card>;
}

const TRAVEL_CHANGE_ACTIONS = {
  submitted: 'Sendt inn',
  resubmitted: 'Sendt inn på nytt',
  updated: 'Endret',
  withdrawn: 'Trukket tilbake',
  approved: 'Godkjent',
  rejected: 'Avvist',
};
const travelChangeText = (entry = {}) => {
  const action = TRAVEL_CHANGE_ACTIONS[entry.action] || 'Endret';
  const who = entry.actor === 'employer' ? 'arbeidsgiver' : 'ansatt';
  return `${action} av ${entry.actorName || who}`;
};
const travelChangeStamp = (value) => {
  const date = new Date(String(value || ''));
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString(PlummeI18n.intlLocale(), { dateStyle: 'short', timeStyle: 'short' });
};
// Er reisen endret etter at den ansatte sendte den inn? Godkjenneren skal se det
// for de godkjenner, ikke oppdage det pa lonnsslippen.
const travelEditedAfterSubmit = (backendTravel = {}) => {
  const submittedAt = String(backendTravel.submittedAt || '');
  if (!submittedAt) return false;
  return (Array.isArray(backendTravel.changeLog) ? backendTravel.changeLog : [])
    .some((entry) => ['updated', 'resubmitted'].includes(entry?.action) && String(entry?.at || '') > submittedAt);
};

const travelPaymentText = (method) => (String(method || '').toLowerCase() === 'company-card' || String(method || '') === 'Firmakort (Driftskonto)' ? 'Firmakort/driftskonto' : 'Privat utlegg');
const travelLineAttachment = (line = {}) => {
  const first = Array.isArray(line.attachments) ? line.attachments[0] : null;
  const attachment = line.attachment || first || null;
  const name = line.attachmentName || attachment?.name || '';
  const downloadUrl = line.attachmentDownloadUrl || attachment?.downloadUrl || attachment?.url || '';
  const storagePath = line.attachmentStoragePath || attachment?.storagePath || '';
  const mimeType = line.attachmentMimeType || attachment?.mimeType || attachment?.type || '';
  const file = attachment?.file || null;
  if (!name && !downloadUrl && !storagePath && !file) return null;
  return { name: name || 'Bilag', downloadUrl, storagePath, mimeType, file };
};

// Bilagsvisning for reiselinjer - gjenbruker forhandsvisningen fra ansattportalen.
function TravelAttachmentPreview({ attachment }) {
  const [preview, setPreview] = useTravelState(null);
  const Viewer = window.AnsattAttachmentBlobPreviewModal;
  const open = async () => {
    let src = attachment?.downloadUrl || '';
    if (!src && attachment?.storagePath && window.PlummeBackend?.getEmployeePortalAttachmentDownloadUrl) {
      try {
        src = await window.PlummeBackend.getEmployeePortalAttachmentDownloadUrl(attachment.storagePath);
      } catch (error) {
        toast(error?.message || t('trv.voucherFetchFailed'), 'error');
        return;
      }
    }
    if (!src && attachment?.file) src = URL.createObjectURL(attachment.file);
    if (!src) {
      toast(t('trv.voucherNoViewLink'), 'warning');
      return;
    }
    if (typeof Viewer !== 'function') {
      window.open(src, '_blank', 'noopener');
      return;
    }
    setPreview({ src, name: attachment?.name || 'Bilag', mimeType: attachment?.mimeType || '' });
  };
  return <React.Fragment>
    <Button variant="secondary" size="sm" icon="eye" onClick={open}>{t('trv.viewVoucher')}</Button>
    {preview && typeof Viewer === 'function' && <Viewer preview={preview} onClose={() => setPreview(null)} />}
  </React.Fragment>;
}

function TravelLineDetailModal({ kind, line = {}, canEdit, onEdit, onClose }) {
  const km = Number(line.kilometers) || 0;
  const attachment = kind === 'expense' ? travelLineAttachment(line) : null;
  const meals = Array.isArray(line.mealsPaidByDate) ? line.mealsPaidByDate : [];
  const rows = kind === 'mileage'
    ? [
      ['Dato', tDate(line.date)],
      ['Rute', [line.fromLocation, ...(Array.isArray(line.stops) ? line.stops : []), line.toLocation].filter(Boolean).join(' → ') || '—'],
      ['Tur/retur', line.returnTrip ? 'Ja' : 'Nei'],
      ['Kilometer', line.returnTrip ? `${km} km ×2 = ${km * 2} km` : `${km} km`],
      ['Kjøretøy', line.vehicle || 'Egen bil'],
      ['Sats', `${(Number(line.ratePerKm) || TRAVEL_RATE).toLocaleString(PlummeI18n.intlLocale(), { minimumFractionDigits: 2 })} kr/km`],
      ['Kilometergodtgjørelse', fmt(Number(line.mileageAmount) || 0)],
      ...(Number(line.tollAmount) > 0 ? [['Bompenger', `${fmt(Number(line.tollAmount))} · ${travelPaymentText(line.tollPaymentMethod)}`]] : []),
      ...((line.passengers || []).length ? [['Passasjerer', (line.passengers || []).map((passenger) => `${passenger.name || 'Passasjer'} (${passenger.kilometers || 0} km)`).join(', ')]] : []),
      ...(line.trailerKilometers ? [['Med tilhenger', `${line.trailerKilometers} km`]] : []),
      ...(line.forestRoadKilometers ? [['Skog- og anleggsvei', `${line.forestRoadKilometers} km`]] : []),
      ['Til lønn', fmt(Number(line.payrollAmount ?? line.mileageAmount) || 0)],
    ]
    : kind === 'accommodation'
      ? [
        ['Periode', line.dateTo && line.dateTo !== line.dateFrom ? `${tDate(line.dateFrom)} – ${tDate(line.dateTo)}` : tDate(line.dateFrom)],
        ['Type opphold', line.lodgingType || line.type || '—'],
        ['Beskrivelse', line.location || line.description || '—'],
        ...(Number(line.ratePerDay) > 0 ? [['Døgnsats', `${Number(line.ratePerDay).toLocaleString(PlummeI18n.intlLocale())} kr`]] : []),
        ['Beregnet diett', fmt(accommodationAmount(line))],
      ]
      : [
        ['Kategori', line.category || 'Utlegg'],
        ['Dato', tDate(line.date)],
        ['Beskrivelse', line.description || '—'],
        ['Betalt med', travelPaymentText(line.paymentMethod)],
        ['Beløp', line.currency && line.currency !== 'NOK' ? `${Number(line.amount) || 0} ${line.currency} · ${Number(line.amountNok) > 0 ? fmt(Number(line.amountNok)) : t('pay.foreignAmount.missing')}` : fmt(Number(line.amountNet ?? line.amount) || 0)],
        ['Bilag', attachment ? attachment.name : 'Ingen bilag'],
      ];
  const title = kind === 'mileage' ? 'Kjøretur' : kind === 'accommodation' ? 'Overnatting' : 'Utlegg';
  return <Modal open onClose={onClose} title={title} max={620} footer={<React.Fragment>
    {canEdit && <Button variant="secondary" icon="pencil" onClick={onEdit}>{t('clo.change')}</Button>}
    <Button onClick={onClose}>{t('pay.close')}</Button>
  </React.Fragment>}>
    <div className="kv">
      {rows.map(([key, value]) => <div key={key} className="kv-row"><span className="kv-key">{key}</span><span className="kv-val">{value}</span></div>)}
    </div>
    {kind === 'accommodation' && meals.length > 0 && <div style={{ marginTop: 14 }}>
      <b className="t-h3" style={{ fontSize: 15 }}>{t('trv.mealsPaidYourself')}</b>
      <div className="col gap-8" style={{ marginTop: 8 }}>
        {meals.map((row) => <div key={row.date} className="row between gap-12 t-sm" style={{ fontWeight: 600 }}>
          <span className="muted">{tDate(row.date)}</span>
          <span>{[row.frokost && 'frokost', row.lunsj && 'lunsj', row.middag && 'middag'].filter(Boolean).join(', ') || 'ingen'}</span>
        </div>)}
      </div>
    </div>}
    {kind === 'expense' && attachment && <div style={{ marginTop: 14 }}><TravelAttachmentPreview attachment={attachment} /></div>}
  </Modal>;
}

function BackendTravelDetail({ travel, onBack, onSaved, onDeleted }) {
  const [dialog, setDialog] = useTravelState(null);
  const [lineView, setLineView] = useTravelState(null);
  const [lineEdit, setLineEdit] = useTravelState(null);
  // Regnskapsforeren ma kunne rette feil kategori, bom eller oppholdstype for
  // godkjenning. Etter godkjenning er reisen grunnlag for lonn og skal ikke endres.
  const editable = travel.status === 'draft' || travel.status === 'submitted';
  const deletable = travel.status === 'draft';
  const tot = travelTotals(travel);
  const [s0, s1] = TRAVEL_STATUS[travel.status] || TRAVEL_STATUS.submitted;
  const backendTravel = travel._backend || {};
  const saveTravel = async (patch, message, change = null) => {
    try {
      const saved = await window.PlummeBackend.saveEmployeePortalTravel(
        { ...backendTravel, ...patch, id: travel.id },
        { change: change || { action: 'updated', actor: 'employer', note: String(message || '').replace(/\.$/, '') } },
      );
      if (message) toast(message, 'success');
      onSaved(saved);
      return saved;
    } catch (error) {
      toast(error?.message || t('trv.travelSaveFailed'), 'error');
      return null;
    }
  };
  const updateTravelStatus = async (status, message, options = {}) => {
    try {
      await window.PlummeBackend.updateEmployeePortalTravelStatus(travel.id, status, options);
      const rejectionNote = status === 'rejected' ? String(options.rejectionNote || '').trim() : '';
      const saved = {
        ...travel,
        status,
        rejectionNote,
        _backend: { ...backendTravel, status, rejectionNote, updatedAt: new Date().toISOString() },
      };
      if (message) toast(message, 'success');
      onSaved(saved);
      return saved;
    } catch (error) {
      toast(error?.message || t('trv.statusUpdateFailed'), 'error');
      return null;
    }
  };
  const deleteTravel = async () => {
    if (!window.confirm(t('trv.deleteConfirm', { purpose: travel.purpose }))) return;
    try {
      await window.PlummeBackend.deleteEmployeePortalTravel(travel.id);
      toast(t('trv.travelDeleted'), 'success');
      onDeleted();
    } catch (error) {
      toast(error?.message || t('trv.travelDeleteFailed'), 'error');
    }
  }; const copyTravel = async () => {
    try {
      const draft = window.PlummeTravelCopyTemplate.buildDraft(backendTravel, { date: todayTravelIso(), purpose: travel.purpose });
      const saved = await window.PlummeBackend.saveEmployeePortalTravel(draft);
      toast(t('trv.travelCopied'), 'success');
      onSaved(saved);
    } catch (error) {
      toast(error?.message || t('trv.travelCopyFailed'), 'error');
    }
  };
  const addMileage = (line) => saveTravel({ mileageLines: [...(backendTravel.mileageLines || []), line] }, 'Kjøretur lagt til.');
  const addExpense = (line) => saveTravel({ expenseLines: [...(backendTravel.expenseLines || []), line] }, 'Utlegg lagt til.');
  const addAccommodation = (line) => saveTravel({ overnight: true, accommodationLines: [...(backendTravel.accommodationLines || []), line] }, 'Overnatting lagt til.');
  const updateLine = (key, line, message) => saveTravel({ [key]: (backendTravel[key] || []).map((row) => (row.id === line.id ? { ...row, ...line } : row)) }, message);
  const changeLog = Array.isArray(backendTravel.changeLog) ? backendTravel.changeLog : [];
  const editedAfterSubmit = travelEditedAfterSubmit(backendTravel);
  const updateMileage = (line) => updateLine('mileageLines', line, 'Kjøretur oppdatert.');
  const updateExpense = (line) => updateLine('expenseLines', line, 'Utlegg oppdatert.');
  const updateAccommodation = (line) => updateLine('accommodationLines', line, 'Overnatting oppdatert.');
  const mileageBackendLine = (row) => row._backend || {
    id: row.id,
    date: row.date,
    fromLocation: row.from,
    toLocation: row.to,
    kilometers: row.km,
    returnTrip: row.returnTrip === true,
    vehicle: row.vehicle,
    ratePerKm: row.rate,
    tollAmount: row.tolls,
    tollPaymentMethod: row.tollsPaidBy === 'Privat utlegg' ? 'private' : 'company-card',
    mileageAmount: row.amount,
  };
  const expenseBackendLine = (row) => row._backend || {
    id: row.id,
    category: row.category,
    date: row.date,
    description: row.description,
    paymentMethod: row.paymentMethod === 'Privat utlegg' ? 'private' : 'company-card',
    amount: row.amount,
  };
  const lineActions = (kind, line) => <React.Fragment>
    <IconButton name="eye" size="sm" variant="ghost" title={t('trv.viewDetails')} aria-label={t('trv.viewDetails')} onClick={() => setLineView({ kind, line })} />
    {editable && <IconButton name="pencil" size="sm" variant="ghost" title={t('trv.editLine')} aria-label={t('trv.editLine')} onClick={() => setLineEdit({ kind, line })} />}
  </React.Fragment>;
  const removeMileage = (lineId) => saveTravel({ mileageLines: (backendTravel.mileageLines || []).filter((line) => line.id !== lineId) }, 'Kjøretur fjernet.');
  const removeExpense = (lineId) => saveTravel({ expenseLines: (backendTravel.expenseLines || []).filter((line) => line.id !== lineId) }, 'Utlegg fjernet.');
  const removeAccommodation = (lineId) => saveTravel({ accommodationLines: (backendTravel.accommodationLines || []).filter((line) => line.id !== lineId) }, 'Overnatting fjernet.');

  const showAccommodation = backendTravel.overnight === true || (travel.accommodation || []).length > 0;

  return <div className="col gap-18">
    <button className="btn btn-ghost" style={{ alignSelf: 'flex-start' }} onClick={onBack}><Icon name="chevLeft" size={18} />{t('trv.backToOverview')}</button>
    <Card>
      <div className="row between wrap gap-12" style={{ alignItems: 'flex-start' }}>
        <div className="row gap-14">
          <span className="emp-avatar" style={{ background: 'var(--plum-100)', color: 'var(--plum-700)' }}><Icon name="car" size={22} /></span>
          <div>
            <div className="row gap-10" style={{ alignItems: 'center', flexWrap: 'wrap' }}><h2 className="t-h1">{travel.purpose || 'Reiseregning'}</h2><Badge variant={s0} dot>{s1}</Badge>{editedAfterSubmit && <Badge variant="warning">{t('trv.editedAfterSubmit')}</Badge>}</div>
            <div className="t-sm muted" style={{ fontWeight: 700, marginTop: 2 }}>{travel.num} · {travel.employee} · {tPeriod(travel)} · {travel.country}</div>
          </div>
        </div>
        <div className="row gap-10 wrap">
          <Button variant="secondary" icon="copy" onClick={copyTravel}>{t('list.copy')}</Button>
          {editable && <Button variant="secondary" icon="pencil" onClick={() => setDialog('edit')}>{t('clo.change')}</Button>}
          {deletable && <Button variant="danger" icon="trash" onClick={deleteTravel}>{t('list.delete')}</Button>}
          {travel.status === 'draft' && <Button icon="send" onClick={() => updateTravelStatus('submitted', 'Reise sendt inn til behandling.')} data-help={t('helpTopics.travelSubmit.body')} data-help-title={t('helpTopics.travelSubmit.title')}>{t('trv.submitTravel')}</Button>}
          {travel.status === 'submitted' && <Button variant="danger" icon="x" onClick={() => setDialog('reject')} data-help={t('helpTopics.travelApprove.body')} data-help-title={t('helpTopics.travelApprove.title')}>{t('trv.reject')}</Button>}
          {travel.status === 'submitted' && <Button icon="check" onClick={() => setDialog('approve')} data-help={t('helpTopics.travelApprove.body')} data-help-title={t('helpTopics.travelApprove.title')}>{travelApprovalLabel(travel)}</Button>}
        </div>
      </div>
    </Card>

    {travel.status === 'rejected' && (travel.rejectionNote || backendTravel.rejectionNote) && <Card style={{ borderColor: 'var(--error-bd)', background: 'var(--error-bg)' }}>
      <div className="row gap-10" style={{ alignItems: 'flex-start' }}>
        <span className="icon-chip" style={{ background: 'var(--card)', color: 'var(--error)' }}><Icon name="x" size={18} /></span>
        <div>
          <div className="t-h3" style={{ fontSize: 15, color: 'var(--error)' }}>{t('pay.rejectedByEmployer')}</div>
          <div className="t-sm" style={{ fontWeight: 700, marginTop: 4, color: 'var(--ink)' }}>{travel.rejectionNote || backendTravel.rejectionNote}</div>
        </div>
      </div>
    </Card>}

    <Card>
      <PaySubHead title={t('trv.trips')} sub={t('trv.tripsHelp')} action={editable && <Button variant="secondary" icon="plus" onClick={() => setDialog('mileage')}>{t('pay.newTrip')}</Button>} />
      {(travel.mileage || []).length === 0
        ? <div className="t-sm muted" style={{ fontWeight: 600 }}>{t('trv.noTrips')}</div>
        : <div className="col gap-10">{travel.mileage.map((m) => <div key={m.id} className="row between gap-12" style={{ padding: '13px 15px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)' }}>
          <div className="row gap-12"><span className="icon-chip" style={{ background: 'var(--panel)' }}><Icon name="car" size={18} /></span>
            <div><div className="t-h3" style={{ fontSize: 14 }}>{m.from} → {m.to}{m.returnTrip ? ' (t/r)' : ''}</div>
              <div className="t-sm muted">{tDate(m.date)} · {m.km}{m.returnTrip ? '×2' : ''} km · {m.vehicle}{m.tolls ? ' · ' + t('trv.tollAmount', { amount: fmt(m.tolls) }) : ''}</div></div></div>
          <div className="row gap-10"><b className="t-num">{fmt(m.amount)}</b>{lineActions('mileage', mileageBackendLine(m))}{editable && <IconButton name="trash" size="sm" variant="ghost" title={t('trv.deleteTrip')} aria-label={t('trv.deleteTrip')} onClick={() => removeMileage(m.id)} />}</div>
        </div>)}</div>}
    </Card>

    {showAccommodation && <Card>
      <PaySubHead title={t('trv.accommodation')} sub={t('trv.subsistenceHelp')} action={editable && <Button variant="secondary" icon="plus" onClick={() => setDialog('accommodation')} data-help={t('helpTopics.subsistence.body')} data-help-title={t('helpTopics.subsistence.title')}>{t('pay.newAccommodation')}</Button>}  help={t('helpTopics.subsistence.body')} helpTitle={t('helpTopics.subsistence.title')}/>
      {(travel.accommodation || []).length === 0
        ? <div className="t-sm muted" style={{ fontWeight: 600 }}>{t('trv.noAccommodation')}</div>
        : <div className="col gap-10">{travel.accommodation.map((line) => <div key={line.id} className="row between gap-12" style={{ padding: '13px 15px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)' }}>
          <div className="row gap-12"><span className="icon-chip" style={{ background: 'var(--panel)' }}><Icon name="home" size={18} /></span>
            <div><div className="t-h3" style={{ fontSize: 14 }}>{line.lodgingType || line.type || 'Opphold'}</div>
              <div className="row gap-8" style={{ flexWrap: 'wrap', alignItems: 'center', marginTop: 2 }}>
                <span className="t-sm muted">{tDate(line.dateFrom)}{line.dateTo && line.dateTo !== line.dateFrom ? ' – ' + tDate(line.dateTo) : ''}{line.location || line.description ? ' · ' + (line.location || line.description) : ''}</span>
                <Badge variant="neutral">{t('pay.subsistence')}</Badge>
              </div></div></div>
          <div className="row gap-10"><b className="t-num">{fmt(accommodationAmount(line))}</b>{lineActions('accommodation', line)}{editable && <IconButton name="trash" size="sm" variant="ghost" title={t('trv.deleteStay')} aria-label={t('trv.deleteStay')} onClick={() => removeAccommodation(line.id)} />}</div>
        </div>)}</div>}
    </Card>}

    <Card>
      <PaySubHead title={t('pay.expenses')} sub={t('trv.expenseHelp')} action={editable && <Button variant="secondary" icon="plus" onClick={() => setDialog('expense')}>{t('pay.newExpense')}</Button>} />
      {(travel.expenses || []).length === 0
        ? <div className="t-sm muted" style={{ fontWeight: 600 }}>{t('trv.noExpenses')}</div>
        : <div className="col gap-10">{travel.expenses.map((e) => <div key={e.id} className="row between gap-12" style={{ padding: '13px 15px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)' }}>
          <div className="row gap-12"><span className="icon-chip" style={{ background: 'var(--panel)' }}><Icon name="receipt" size={18} /></span>
            <div><div className="t-h3" style={{ fontSize: 14 }}>{e.category}</div>
              <div className="row gap-8" style={{ flexWrap: 'wrap', alignItems: 'center', marginTop: 2 }}>
                <span className="t-sm muted">{tDate(e.date)} · {e.description}</span>
                <Badge variant={e.paymentMethod === 'Privat utlegg' ? 'warning' : 'muted'}>{e.paymentMethod === 'Privat utlegg' ? 'Privat' : 'Driftskonto'}</Badge>
                {e.hasAttachment && <span className="t-xs" style={{ color: 'var(--success)', fontWeight: 700 }}>{t('trv.attachmentMark')}</span>}
              </div></div></div>
          <div className="row gap-10"><b className="t-num">{fmt(e.amount)}</b>{lineActions('expense', expenseBackendLine(e))}{editable && <IconButton name="trash" size="sm" variant="ghost" title={t('trv.deleteExpense')} aria-label={t('trv.deleteExpense')} onClick={() => removeExpense(e.id)} />}</div>
        </div>)}</div>}
    </Card>

    {changeLog.length > 0 && <Card>
      <PaySubHead title={t('trv.history')} sub={t('trv.historyHelp')} />
      <div className="col gap-8">
        {[...changeLog].reverse().map((entry, index) => <div key={`${entry.at}-${index}`} className="row between gap-12 t-sm" style={{ fontWeight: 600 }}>
          <span>{travelChangeText(entry)}{entry.note ? ` — ${entry.note}` : ''}</span>
          <span className="muted" style={{ flex: 'none' }}>{travelChangeStamp(entry.at)}</span>
        </div>)}
      </div>
    </Card>}

    <Card style={{ background: 'var(--panel)' }}>
      <div className="row between" style={{ marginBottom: 12 }}><div className="row gap-10"><span className="icon-chip"><Icon name="wallet" size={18} /></span><h3 className="t-h2" data-help={t('helpTopics.travelApprove.body')} data-help-title={t('helpTopics.travelApprove.title')}>{t('trv.toPayroll')}</h3></div><div className="t-display" style={{ fontSize: 26, color: 'var(--plum-700)' }}>{fmt(tot.reimb)}</div></div>
      <div className="kv">
        <div className="kv-row"><span className="kv-key">{t('trv.mileageAllowance')}</span><span className="kv-val t-num">{fmt(tot.mil)}</span></div>
        {tot.privTolls > 0 && <div className="kv-row"><span className="kv-key">{t('trv.tollsPrivate')}</span><span className="kv-val t-num">{fmt(tot.privTolls)}</span></div>}
        {tot.accom > 0 && <div className="kv-row"><span className="kv-key">{t('trv.subsistenceOvernight')}</span><span className="kv-val t-num">{fmt(tot.accom)}</span></div>}
        <div className="kv-row"><span className="kv-key">{t('trv.privateExpenses')}</span><span className="kv-val t-num">{fmt(tot.priv)}</span></div>
      </div>
      {tot.company > 0 && <div className="t-sm muted mt-12" style={{ fontWeight: 600 }}>{t('trv.companyPaidDirect', { amount: fmt(tot.company) })}</div>}
    </Card>

    {dialog === 'mileage' && <BackendMileageDialog onClose={() => setDialog(null)} onSave={(m) => { addMileage(m); setDialog(null); }} />}
    {dialog === 'accommodation' && <BackendAccommodationDialog onClose={() => setDialog(null)} onSave={(line) => { addAccommodation(line); setDialog(null); }} />}
    {dialog === 'expense' && <ExpenseDialog onClose={() => setDialog(null)} onSave={(e) => { addExpense(e); setDialog(null); }} />}
    {dialog === 'edit' && <EditTravelDialog travel={travel} onClose={() => setDialog(null)} onSave={(patch) => { saveTravel(patch, 'Reise oppdatert.'); setDialog(null); }} />}
    {dialog === 'approve' && <ApproveDialog travel={travel} totals={tot} onClose={() => setDialog(null)} onApprove={() => updateTravelStatus('approved', 'Reise godkjent.').then((saved) => { if (saved) onBack(); })} />}
    {dialog === 'reject' && <RejectTravelDialog travel={travel} onClose={() => setDialog(null)} onReject={(rejectionNote) => updateTravelStatus('rejected', 'Reise avvist.', { rejectionNote }).then((saved) => { if (saved) onBack(); })} />}
    {lineView && <TravelLineDetailModal
      kind={lineView.kind}
      line={lineView.line}
      canEdit={editable}
      onEdit={() => { setLineEdit(lineView); setLineView(null); }}
      onClose={() => setLineView(null)}
    />}
    {lineEdit?.kind === 'mileage' && <BackendMileageDialog line={lineEdit.line} onClose={() => setLineEdit(null)} onSave={(line) => { updateMileage(line); setLineEdit(null); }} />}
    {lineEdit?.kind === 'accommodation' && <BackendAccommodationDialog line={lineEdit.line} onClose={() => setLineEdit(null)} onSave={(line) => { updateAccommodation(line); setLineEdit(null); }} />}
    {lineEdit?.kind === 'expense' && <ExpenseDialog line={lineEdit.line} onClose={() => setLineEdit(null)} onSave={(line) => { updateExpense(line); setLineEdit(null); }} />}
  </div>;
}

function BackendNewTravelDialog({ onClose, onCreate }) {
  const firstEmployee = employees()[0] || {};
  const [draft, setDraft] = useTravelState(() => tripFormDefaults({
    employee: firstEmployee.name || '',
    date: todayTravelIso(),
    dateFrom: todayTravelIso(),
    dateTo: todayTravelIso(),
  }));
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const save = async () => {
    const employee = employees().find((entry) => entry.name === draft.employee);
    if (!employee) { toast(t('trv.pickEmployee'), 'warning'); return; }
    if (!tripFormValid(draft)) { toast(t('trv.purposeRequired'), 'warning'); return; }
    const dates = tripDates(draft);
    try {
      const saved = await window.PlummeBackend.saveEmployeePortalTravel({
        employeeId: employee.id,
        employeeName: employee.name,
        purpose: draft.purpose.trim(),
        destination: draft.country,
        country: draft.country,
        dateFrom: dates.dateFrom,
        dateTo: dates.dateTo,
        travelType: draft.diet === 'yes' ? 'diet' : 'mixed',
        hasDiet: draft.diet === 'yes',
        overnight: draft.overnight === 'yes',
        timeFrom: draft.timeFrom || '',
        timeTo: draft.timeTo || '',
        coveredMeals: draft.meals || {},
        status: 'draft',
        mileageLines: [],
        expenseLines: [],
        accommodationLines: [],
      });
      toast(t('trv.travelSaved'), 'success');
      onCreate(saved);
    } catch (error) {
      toast(error?.message || t('trv.travelSaveFailed'), 'error');
    }
  };
  return <PayDialog open onClose={onClose} title={t('trv.newTravel')} saveLabel="Registrer reise" onSave={save}>
    <TravelTripFields v={draft} set={set} showEmployee employees={employees()} ns="pay" />
  </PayDialog>;
}

function EditTravelDialog({ travel, onClose, onSave }) {
  const [draft, setDraft] = useTravelState({ purpose: travel.purpose || '', destination: travel.country || 'Norge', dateFrom: travel.dateFrom, dateTo: travel.dateTo, hasDiet: travel._backend?.hasDiet === true, overnight: travel._backend?.overnight === true || (travel.accommodation || []).length > 0 });
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  return <PayDialog open onClose={onClose} title={t('trv.editTravel')} saveLabel="Lagre" onSave={() => onSave(draft)}>
    <Field label={t('pay.purpose')} help={t('helpTopics.tripPurpose.body')} helpTitle={t('helpTopics.tripPurpose.title')}><Input value={draft.purpose} onChange={(e) => set('purpose', e.target.value)} /></Field>
    <div className="grid-2 gap-14">
      <Field label={t('set.country')} help={t('helpTopics.destination.body')} helpTitle={t('helpTopics.destination.title')}><Select value={draft.destination} onChange={(e) => set('destination', e.target.value)}>{TRAVEL_COUNTRIES.map((country) => <option key={country}>{country}</option>)}</Select></Field>
      <Field label={t('pay.dateFrom')}><Input type="date" value={draft.dateFrom} onChange={(e) => set('dateFrom', e.target.value)} /></Field>
      <Field label={t('pay.dateTo')}><Input type="date" value={draft.dateTo} onChange={(e) => set('dateTo', e.target.value)} /></Field>
    </div>
    <div className="row wrap gap-12">
      <label className="row gap-10" style={{ fontWeight: 700, cursor: 'pointer' }}><Switch on={draft.hasDiet} onChange={(value) => set('hasDiet', value)} /> {t('trv.calculateSubsistence')}</label>
      <label className="row gap-10" style={{ fontWeight: 700, cursor: 'pointer' }}><Switch on={draft.overnight} onChange={(value) => set('overnight', value)} /> {TRAVEL_OVERNIGHT_LABEL}</label>
    </div>
  </PayDialog>;
}

function BackendMileageDialog({ onClose, onSave, line }) {
  const [draft, setDraft] = useTravelState({
    date: todayTravelIso(),
    fromLocation: '',
    toLocation: '',
    stops: [],
    kilometers: '',
    returnTrip: false,
    vehicle: TRAVEL_VEHICLES[0],
    tollAmount: '',
    tollPaymentMethod: 'private',
    passengers: [],
    trailerKilometers: null,
    forestRoadKilometers: null,
    ...(line ? {
      date: line.date || todayTravelIso(),
      fromLocation: line.fromLocation || '',
      toLocation: line.toLocation || '',
      stops: Array.isArray(line.stops) ? line.stops : [],
      kilometers: line.kilometers == null ? '' : String(line.kilometers),
      returnTrip: line.returnTrip === true,
      vehicle: line.vehicle || TRAVEL_VEHICLES[0],
      tollAmount: Number(line.tollAmount) > 0 ? String(line.tollAmount) : '',
      tollPaymentMethod: line.tollPaymentMethod || line.paymentMethod || 'private',
      passengers: Array.isArray(line.passengers) ? line.passengers : [],
      trailerKilometers: line.trailerKilometers == null ? null : String(line.trailerKilometers),
      forestRoadKilometers: line.forestRoadKilometers == null ? null : String(line.forestRoadKilometers),
    } : {}),
  });
  // Adressesøk + automatisk km-beregning for strekningen.
  const [activeRouteField, setActiveRouteField] = React.useState(null);
  const [routeSuggestions, setRouteSuggestions] = React.useState({});
  const [routeLoading, setRouteLoading] = React.useState(false);
  const [routeStatus, setRouteStatus] = React.useState('');
  const [kilometersManual, setKilometersManual] = React.useState(Boolean(line));
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const parseDecimal = (value) => Number(String(value ?? '').replace(',', '.')) || 0;
  const km = parseDecimal(draft.kilometers);
  const tollAmount = parseDecimal(draft.tollAmount);
  const rate = line?.ratePerKm ?? TRAVEL_RATE; const amount = Math.round(km * (draft.returnTrip ? 2 : 1) * rate * 100) / 100;
  const passengerLines = (draft.passengers || [])
    .map((passenger) => ({ name: String(passenger.name || '').trim(), kilometers: parseDecimal(passenger.kilometers) }))
    .filter((passenger) => passenger.name || passenger.kilometers > 0);
  const trailerKilometers = draft.trailerKilometers == null ? undefined : parseDecimal(draft.trailerKilometers);
  const forestRoadKilometers = draft.forestRoadKilometers == null ? undefined : parseDecimal(draft.forestRoadKilometers);
  const routePoints = [draft.fromLocation, ...(draft.stops || []), draft.toLocation].map((point) => String(point || '').trim()).filter(Boolean);
  React.useEffect(() => {
    if (!activeRouteField) return undefined;
    const query = activeRouteField === 'fromLocation'
      ? draft.fromLocation
      : activeRouteField === 'toLocation'
        ? draft.toLocation
        : draft.stops?.[Number(String(activeRouteField).replace('stop-', ''))] || '';
    const text = String(query || '').trim();
    if (text.length < 3 || !window.PlummeBackend?.searchMileageAddresses) {
      setRouteSuggestions((current) => ({ ...current, [activeRouteField]: [] }));
      setRouteLoading(false);
      return undefined;
    }
    let cancelled = false;
    setRouteLoading(true);
    const timer = window.setTimeout(() => {
      Promise.resolve(window.PlummeBackend.searchMileageAddresses({ query: text }))
        .then((items) => {
          if (!cancelled) setRouteSuggestions((current) => ({ ...current, [activeRouteField]: Array.isArray(items) ? items : [] }));
        })
        .catch(() => {
          if (!cancelled) setRouteSuggestions((current) => ({ ...current, [activeRouteField]: [] }));
        })
        .finally(() => {
          if (!cancelled) setRouteLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [activeRouteField, draft.fromLocation, draft.toLocation, (draft.stops || []).join('|')]);
  React.useEffect(() => {
    if (kilometersManual) return undefined;
    if (routePoints.length < 2 || routePoints.some((point) => point.length < 3) || !window.PlummeBackend?.calculateMileageDistance) {
      setRouteStatus('');
      return undefined;
    }
    let cancelled = false;
    setRouteStatus('Beregner kilometer...');
    const timer = window.setTimeout(() => {
      const legs = routePoints.slice(0, -1).map((point, index) => [point, routePoints[index + 1]]);
      Promise.all(legs.map(([fromAddress, toAddress]) => window.PlummeBackend.calculateMileageDistance({ fromAddress, toAddress })))
        .then((results) => {
          if (cancelled) return;
          const kilometers = results.reduce((sum, result) => sum + Math.max(0, Number(result?.kilometers) || 0), 0);
          if (kilometers > 0) {
            set('kilometers', kilometers.toLocaleString(PlummeI18n.intlLocale(), { maximumFractionDigits: 1 }));
            setRouteStatus('Kilometer beregnet automatisk. Du kan overskrive feltet.');
            return;
          }
          setRouteStatus('Fant ikke automatisk kilometer. Fyll inn manuelt.');
        })
        .catch(() => {
          if (!cancelled) setRouteStatus('Fant ikke automatisk kilometer. Fyll inn manuelt.');
        });
    }, 650);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [routePoints.join('|'), kilometersManual]);
  const setRouteValue = (field, value) => {
    setKilometersManual(false);
    if (field === 'fromLocation' || field === 'toLocation') {
      set(field, value);
      return;
    }
    const index = Number(String(field).replace('stop-', ''));
    const stops = [...(draft.stops || [])];
    stops[index] = value;
    set('stops', stops);
  };
  const chooseRouteSuggestion = (field, item) => {
    const label = item?.displayName || item?.label || '';
    setRouteValue(field, label);
    setRouteSuggestions((current) => ({ ...current, [field]: [] }));
    setActiveRouteField(null);
  };
  const routeInput = (field, value, placeholder) => {
    const suggestions = routeSuggestions[field] || [];
    return <div className="ans-route-lookup">
      <Input value={value} onFocus={() => setActiveRouteField(field)} onChange={(event) => { setRouteValue(field, event.target.value); setActiveRouteField(field); }} placeholder={placeholder} />
      {activeRouteField === field && (routeLoading || suggestions.length > 0) && <React.Fragment>
        <div className="pop-scrim" onClick={() => setActiveRouteField(null)} />
        <div className="pop pop-menu ans-route-menu">
          {routeLoading && !suggestions.length
            ? <button type="button">{t('km.searching')}</button>
            : suggestions.map((item) => <button key={item.id} type="button" onClick={() => chooseRouteSuggestion(field, item)}><Icon name="search" size={16} /><span>{item.label || item.displayName}</span></button>)}
        </div>
      </React.Fragment>}
    </div>;
  };
  const save = () => onSave({
    id: line?.id || genId('k_'),
    date: draft.date,
    fromLocation: draft.fromLocation,
    toLocation: draft.toLocation,
    stops: (draft.stops || []).map((stop) => String(stop || '').trim()).filter(Boolean),
    kilometers: km,
    returnTrip: draft.returnTrip,
    vehicle: draft.vehicle,
    ratePerKm: rate, ...(line?.workflowVersion === 2 ? { workflowVersion: 2, ratePolicy: line.ratePolicy, vehicleId: line.vehicleId, sourceMileageId: line.sourceMileageId } : {}),
    tollAmount,
    tollPaymentMethod: draft.tollPaymentMethod,
    passengers: passengerLines,
    trailerKilometers,
    forestRoadKilometers,
    mileageAmount: amount,
    payrollAmount: amount + (draft.tollPaymentMethod === 'private' ? tollAmount : 0),
  });

  return <PayDialog open onClose={onClose} title={line ? 'Rediger kjøretur' : t('pay.newTrip')} saveLabel={line ? 'Lagre' : 'Registrer'} onSave={save}>
    <Field label={t('trv.pickDate')}><Input type="date" value={draft.date} onChange={(e) => set('date', e.target.value)} /></Field>

    <Field label={t('trv.route')} style={{ marginTop: 14 }} help={t('helpTopics.tripDistance.body')} helpTitle={t('helpTopics.tripDistance.title')}>
      <div className="col gap-10">
        {routeInput('fromLocation', draft.fromLocation, 'Fra')}
        {(draft.stops || []).map((stop, index) => <div key={index} className="row gap-10">
          {routeInput('stop-' + index, stop, 'Stopp ' + (index + 1))}
          <Button variant="secondary" onClick={() => { setKilometersManual(false); set('stops', draft.stops.filter((_, current) => current !== index)); }}>{t('list.delete')}</Button>
        </div>)}
        {routeInput('toLocation', draft.toLocation, 'Til')}
        <div><Button variant="secondary" onClick={() => { setKilometersManual(false); set('stops', [...(draft.stops || []), '']); }}>{t('trv.addStop')}</Button></div>
      </div>
    </Field>

    <div className="grid-2 gap-14" style={{ marginTop: 14, alignItems: 'end' }}>
      <Field label={t('trv.kilometreCount')} hint={routeStatus || undefined} help={t('helpTopics.tripDistance.body')} helpTitle={t('helpTopics.tripDistance.title')}><Input inputMode="decimal" value={draft.kilometers} onChange={(e) => { setKilometersManual(true); set('kilometers', e.target.value); }} placeholder="0" /></Field>
      <div style={{ paddingBottom: 12 }}><Checkbox checked={draft.returnTrip} onChange={(value) => set('returnTrip', value)}>{t('trv.sameRouteReturn')}</Checkbox></div>
    </div>

    <Field label={t('trv.pickVehicle')} hint={t('km.ratePerKm') + ': ' + rate.toLocaleString(PlummeI18n.intlLocale(), { minimumFractionDigits: 2 }) + ' kr/km'} style={{ marginTop: 14 }} help={t('helpTopics.vehiclePolicy.body')} helpTitle={t('helpTopics.vehiclePolicy.title')}>
      <Select value={draft.vehicle} onChange={(e) => set('vehicle', e.target.value)}>{TRAVEL_VEHICLES.map((vehicle) => <option key={vehicle}>{vehicle}</option>)}</Select>
    </Field>

    <div className="hairline" style={{ margin: '14px 0' }} />

    <div className="grid-2 gap-14" style={{ alignItems: 'end' }}>
      <Field label={t('trv.tollsNok')}><Currency value={draft.tollAmount} onChange={(e) => set('tollAmount', e.target.value)} placeholder="0" /></Field>
      <div className="row gap-10" style={{ alignItems: 'flex-end' }}>
        <Field label={t('trv.paymentMethod')} className="grow" help={t('helpTopics.privatePayment.body')} helpTitle={t('helpTopics.privatePayment.title')}><Select value={draft.tollPaymentMethod} onChange={(e) => set('tollPaymentMethod', e.target.value)}><option value="private">{t('inbox.payPrivate')}</option><option value="company-card">{t('trv.companyCardOperating')}</option></Select></Field>
        <Button variant="secondary" onClick={() => set('tollAmount', '')}>{t('list.delete')}</Button>
      </div>
    </div>

    {(draft.passengers || []).map((passenger, index) => <div key={index} className="grid-2 gap-14" style={{ marginTop: 12, alignItems: 'end' }}>
      <Field label={t('trv.enterPassengerName')} help={t('helpTopics.mileageExtra.body')} helpTitle={t('helpTopics.mileageExtra.title')}><Input value={passenger.name} onChange={(e) => {
        const passengers = [...(draft.passengers || [])];
        passengers[index] = { ...passengers[index], name: e.target.value };
        set('passengers', passengers);
      }} placeholder={t('cust.form.contactName')} /></Field>
      <div className="row gap-10" style={{ alignItems: 'flex-end' }}>
        <Field label={t('trv.enterKilometres')} className="grow" help={t('helpTopics.mileageExtra.body')} helpTitle={t('helpTopics.mileageExtra.title')}><Input inputMode="decimal" value={passenger.kilometers} onChange={(e) => {
          const passengers = [...(draft.passengers || [])];
          passengers[index] = { ...passengers[index], kilometers: e.target.value };
          set('passengers', passengers);
        }} placeholder={t('trv.kilometreCount')} /></Field>
        <Button variant="secondary" onClick={() => set('passengers', draft.passengers.filter((_, current) => current !== index))}>{t('list.delete')}</Button>
      </div>
    </div>)}

    {draft.trailerKilometers != null && <div className="row gap-10" style={{ marginTop: 12, alignItems: 'flex-end' }}>
      <Field label={t('trv.kmWithTrailer')} className="grow" help={t('helpTopics.mileageExtra.body')} helpTitle={t('helpTopics.mileageExtra.title')}><Input inputMode="decimal" value={draft.trailerKilometers} onChange={(e) => set('trailerKilometers', e.target.value)} placeholder="0" /></Field>
      <Button variant="secondary" onClick={() => set('trailerKilometers', null)}>{t('list.delete')}</Button>
    </div>}

    {draft.forestRoadKilometers != null && <div className="row gap-10" style={{ marginTop: 12, alignItems: 'flex-end' }}>
      <Field label={t('trv.kmForestRoad')} className="grow" help={t('helpTopics.mileageExtra.body')} helpTitle={t('helpTopics.mileageExtra.title')}><Input inputMode="decimal" value={draft.forestRoadKilometers} onChange={(e) => set('forestRoadKilometers', e.target.value)} placeholder="0" /></Field>
      <Button variant="secondary" onClick={() => set('forestRoadKilometers', null)}>{t('list.delete')}</Button>
    </div>}

    <div className="hairline" style={{ margin: '14px 0' }} />
    <div className="row gap-10" style={{ flexWrap: 'wrap' }}>
      <Button variant="secondary" onClick={() => set('passengers', [...(draft.passengers || []), { name: '', kilometers: '' }])}>{t('trv.addPassenger')}</Button>
      <Button variant="secondary" onClick={() => set('trailerKilometers', draft.trailerKilometers == null ? '' : draft.trailerKilometers)}>{t('trv.addTrailerKm')}</Button>
      <Button variant="secondary" onClick={() => set('forestRoadKilometers', draft.forestRoadKilometers == null ? '' : draft.forestRoadKilometers)}>{t('trv.addForestRoadKm')}</Button>
    </div>
  </PayDialog>;
}

function BackendAccommodationDialog({ onClose, onSave, line }) {
  const [draft, setDraft] = useTravelState({
    dateFrom: todayTravelIso(),
    dateTo: todayTravelIso(),
    lodgingType: '',
    location: '',
    meals: {},
    ...(line ? {
      dateFrom: line.dateFrom || todayTravelIso(),
      dateTo: line.dateTo || line.dateFrom || todayTravelIso(),
      lodgingType: line.lodgingType || line.type || '',
      location: line.location || line.description || '',
      meals: line.meals || {},
    } : {}),
  });
  const set = (patch) => setDraft((current) => ({ ...current, ...patch }));
  const dates = travelDatesBetween(draft.dateFrom, draft.dateTo);
  const datesOk = dates.length > 0;
  const mealVal = (date, meal) => {
    const row = (draft.meals || {})[date];
    return row ? row[meal] !== false : true;
  };
  const setMeal = (date, meal, checked) => setDraft((current) => {
    const meals = { ...(current.meals || {}) };
    const currentRow = {
      frokost: mealVal(date, 'frokost'),
      lunsj: mealVal(date, 'lunsj'),
      middag: mealVal(date, 'middag'),
      ...(meals[date] || {}),
    };
    meals[date] = { ...currentRow, [meal]: checked };
    return { ...current, meals };
  });
  const allMeals = dates.length > 0 && dates.every((date) => mealVal(date, 'frokost') && mealVal(date, 'lunsj') && mealVal(date, 'middag'));
  const toggleAll = (checked) => setDraft((current) => {
    const meals = {};
    dates.forEach((date) => { meals[date] = { frokost: checked, lunsj: checked, middag: checked }; });
    return { ...current, meals };
  });
  const rate = OPPHOLD_RATES[draft.lodgingType] || 0;
  const amount = accommodationDiet(draft.lodgingType, dates, mealVal);
  const save = () => {
    if (!datesOk || !draft.lodgingType) {
      toast(t('trv.pickDateAndStayType'), 'warning');
      return;
    }
    if (!String(draft.location || '').trim()) {
      toast(t('trv.lodgingDescriptionRequired'), 'warning');
      return;
    }
    onSave({
      id: line?.id || genId('o_'),
      dateFrom: draft.dateFrom,
      dateTo: draft.dateTo || draft.dateFrom,
      lodgingType: draft.lodgingType,
      type: draft.lodgingType,
      location: String(draft.location || '').trim(),
      description: String(draft.location || '').trim() || 'Overnatting',
      mealsPaidByDate: dates.map((date) => ({
        date,
        frokost: mealVal(date, 'frokost'),
        lunsj: mealVal(date, 'lunsj'),
        middag: mealVal(date, 'middag'),
      })),
      meals: draft.meals,
      ratePerDay: rate,
      allowanceAmount: amount,
      payrollAmount: amount,
      amount,
      paymentMethod: 'private',
    });
  };

  return <PayDialog open onClose={onClose} title={line ? 'Rediger opphold' : t('trv.newStay')} saveLabel={line ? 'Lagre' : 'Registrer'} onSave={save}>
    <div className="grid-2 gap-14">
      <Field label={t('ui.fromDate')}><Input type="date" value={draft.dateFrom} onChange={(event) => set({ dateFrom: event.target.value })} /></Field>
      <Field label={t('ui.toDate')}><Input type="date" value={draft.dateTo} onChange={(event) => set({ dateTo: event.target.value })} /></Field>
    </div>
    <Field label={t('trv.stayType')} hint={t('trv.stayTypeHint')} style={{ marginTop: 14 }} help={t('helpTopics.subsistence.body')} helpTitle={t('helpTopics.subsistence.title')}>
      <Select value={draft.lodgingType} onChange={(event) => set({ lodgingType: event.target.value })}>
        <option value="">{t('clo.pick')}</option>
        {OPPHOLD_TYPES.map((type) => <option key={type}>{type}</option>)}
      </Select>
    </Field>
    {draft.lodgingType === 'Annen overnatting u/kokemuligheter' && <div className="alert" style={{ background: 'var(--plum-50)', border: '1px solid var(--plum-200)', borderRadius: 'var(--r-ms)', padding: '12px 14px', marginTop: 4 }}><span className="t-sm" style={{ fontWeight: 600, color: 'var(--ink-2)' }}>{t('trv.otherStayHintLead')} <b style={{ color: 'var(--ink)' }}>{t('trv.otherAccommodationCooking')}</b>.</span></div>}
    <Field label={t('sale.lines.description')} hint={t('trv.lodgingHint')} style={{ marginTop: 14 }}><Input value={draft.location} onChange={(event) => set({ location: event.target.value })} /></Field>
    {draft.lodgingType && datesOk && <div className="ans-opphold-meals">
      <div className="hairline" style={{ margin: '6px 0 14px' }} />
      <b className="t-h3" style={{ fontSize: 15 }}>{t('trv.mealsPaidYourself')}</b>
      <p className="t-sm muted" style={{ fontWeight: 600, margin: '4px 0 12px' }}>{t('trv.mealsHint')}</p>
      <div style={{ marginBottom: 8 }}><Checkbox checked={allMeals} onChange={toggleAll}>{t('trv.pickForWholeStay')}</Checkbox></div>
      {dates.map((date) => <div key={date} className="ans-meal-row">
        <span className="ans-meal-date">{tDate(date)}</span>
        <Checkbox checked={mealVal(date, 'frokost')} onChange={(checked) => setMeal(date, 'frokost', checked)}>{t('trv.breakfast')}</Checkbox>
        <Checkbox checked={mealVal(date, 'lunsj')} onChange={(checked) => setMeal(date, 'lunsj', checked)}>{t('trv.lunch')}</Checkbox>
        <Checkbox checked={mealVal(date, 'middag')} onChange={(checked) => setMeal(date, 'middag', checked)}>{t('trv.dinner')}</Checkbox>
      </div>)}
      <div className="ans-opphold-diett"><span>{t('trv.calculatedSubsistence', { rate: rate.toLocaleString(PlummeI18n.intlLocale()) })}</span><b className="t-num">{amount.toLocaleString(PlummeI18n.intlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr</b></div>
    </div>}
  </PayDialog>;
}

function ExpenseDialog({ onClose, onSave, standalone, line }) {
  const firstEmployee = employees()[0] || {};
  const [draft, setDraft] = useTravelState({
    employeeId: firstEmployee.id || '',
    category: EXPENSE_CATEGORIES[0],
    date: todayTravelIso(),
    description: '',
    paymentMethod: 'private',
    amount: '',
    vatRate: '0',
    attachments: [],
    ...(line ? {
      category: line.category || EXPENSE_CATEGORIES[0],
      date: line.date || todayTravelIso(),
      description: line.description || '',
      paymentMethod: String(line.paymentMethod || '').toLowerCase() === 'company-card' ? 'company-card' : 'private',
      // Beløpet i skjemaet er kvitteringens totalsum (brutto); nettobeløpet
      // utledes av mva-satsen ved lagring.
      amount: (line.amount ?? line.amountNet) == null ? '' : String(line.amount ?? line.amountNet),
      vatRate: String(Number(line.vatRate) || 0), amountNok: Number(line.amountNok) > 0 ? String(line.amountNok) : '',
      attachments: Array.isArray(line.attachments) && line.attachments.length ? line.attachments : (line.attachment ? [line.attachment] : []),
    } : {}),
  });
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const amount = Number(String(draft.amount).replace(',', '.')) || 0;
  const attachments = Array.isArray(draft.attachments) ? draft.attachments : [];
  const valid = draft.description.trim() && amount > 0 && attachments.length > 0;
  const chooseFile = (event) => {
    const file = Array.from(event.target.files || [])[0];
    if (!file) return;
    set('attachments', [{
      id: genId('att_'),
      name: file.name || 'Kvittering',
      type: file.type || '',
      mimeType: file.type || '',
      size: file.size || 0,
      file,
      source: 'upload',
    }]);
    event.target.value = '';
  };
  const save = () => {
    if (!valid) { toast(t('trv.expenseRequirements'), 'warning'); return; }
    const employee = employees().find((entry) => entry.id === draft.employeeId);
    // Mva-satsen leses av kvitteringen. Med sats > 0 skilles dokumentert
    // inngående mva ut i lønnsposteringen (mval. kap. 8); uten sats bokføres
    // brutto som før. Firmakort-linjer konteres i innboksen og bærer ingen sats her.
    const vatRate = draft.paymentMethod === 'private' ? Number(draft.vatRate) || 0 : 0;
    const amountNet = vatRate > 0 ? Math.round((amount / (1 + vatRate / 100)) * 100) / 100 : amount;
    onSave({
      ...(line || {}),
      id: line?.id || genId('e_'),
      category: draft.category,
      date: draft.date,
      description: draft.description.trim(),
      paymentMethod: draft.paymentMethod,
      amount, amountNok: window.PlummeMoneyInput.parse(draft.amountNok, { preferDecimal: true }) || undefined,
      amountNet,
      vatRate,
      vatAmount: Math.round((amount - amountNet) * 100) / 100,
      vatTreatment: vatRate > 0 ? 'standard' : 'zeroRated',
      hasAttachment: attachments.length > 0,
      attachments,
    }, employee);
  };
  return <PayDialog open onClose={onClose} title={line ? 'Rediger utlegg' : standalone ? 'Nytt utlegg' : 'Nytt utlegg på reise'} saveLabel={line ? 'Lagre' : 'Registrer utlegg'} onSave={save}>
    {standalone && <Field label={t('pay.employee')}><Select value={draft.employeeId} onChange={(e) => set('employeeId', e.target.value)}>{employees().map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}</Select></Field>}
    <div className="grid-2 gap-14">
      <Field label={t('buy.category')}><Select value={draft.category} onChange={(e) => set('category', e.target.value)}>{EXPENSE_CATEGORIES.map((category) => <option key={category}>{category}</option>)}</Select></Field>
      <Field label={t('cust.ledger.date')}><Input type="date" value={draft.date} onChange={(e) => set('date', e.target.value)} /></Field>
      <Field label={t('trv.paymentMethod2')} help={t('helpTopics.privatePayment.body')} helpTitle={t('helpTopics.privatePayment.title')}><Select value={draft.paymentMethod} onChange={(e) => set('paymentMethod', e.target.value)}><option value="private">{t('inbox.payPrivate')}</option><option value="company-card">{t('trv.companyCardOperating')}</option></Select></Field>
      <Field label={t('inv.kpi.amount')}><Currency value={draft.amount} onChange={(e) => set('amount', e.target.value)} placeholder="0" /></Field><ForeignAmountNokField currency={line?.currency} amount={draft.amount} value={draft.amountNok} onChange={(value) => set('amountNok', value)} />
      {draft.paymentMethod === 'private' && window.PlummeBackend?.appState?.settings?.vatRegistered !== false
        && <Field label={t('trv.vatOnReceipt', 'Mva på kvitteringen')} help={t('helpTopics.vatReceipt.body')} helpTitle={t('helpTopics.vatReceipt.title')}>
          <Select value={draft.vatRate} onChange={(e) => set('vatRate', e.target.value)}>
            <option value="0">{t('trv.vatNone', 'Ingen / ikke fradrag')}</option>
            <option value="25">25 %</option>
            <option value="15">15 %</option>
            <option value="12">12 %</option>
          </Select></Field>}
    </div>
    <Field label={t('sale.lines.description')}><Input value={draft.description} onChange={(e) => set('description', e.target.value)} placeholder={t('pay.whatIsExpenseFor')} /></Field>
    <label className="btn btn-secondary" style={{ alignSelf: 'flex-start', borderStyle: attachments.length ? 'solid' : 'dashed', cursor: 'pointer' }}>
      <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={chooseFile} />
      <Icon name={attachments.length ? 'check' : 'upload'} size={16} />{attachments.length ? attachments[0].name : 'Legg ved kvittering'}
    </label>
    {attachments.length > 0 && <Button variant="secondary" size="sm" onClick={() => set('attachments', [])}>{t('asset.removeAttachment')}</Button>}
    {!valid && <div className="t-xs muted" style={{ fontWeight: 700 }}>{t('trv.requiredBeforeRegister')}</div>}
    {draft.paymentMethod !== 'private' && <div className="t-sm muted" style={{ fontWeight: 600 }}>{t('trv.companyCardDirectHelp')}</div>}
  </PayDialog>;
}

Object.assign(window, { PayTravel, BackendMileageDialog, BackendAccommodationDialog });
